/**
 * Tab 1: System Health
 * At-a-glance operational status for all service dependencies.
 */

import React from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import { WebhookPanel } from '../shared/WebhookPanel';
import { LoadingPanel, ErrorPanel } from '../shared/LoadingPanel';
import { useSystemHealth, useWebhookSubscriptions, formatRelativeTime } from '../hooks/useAdminData';
import type { ServiceStatus } from '../../../types/admin';

const SERVICE_LABELS: Record<string, string> = {
  openai: 'Azure OpenAI',
  search: 'Azure AI Search',
  storage: 'Table Storage',
  redis: 'Redis Cache',
};

export const SystemHealth: React.FC = () => {
  const health = useSystemHealth(60_000);
  const webhooks = useWebhookSubscriptions(60_000);

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">System Health</h2>
        <span className="adm-refresh-label">
          Updated {formatRelativeTime(health.lastRefreshed)}
        </span>
      </div>

      {health.loading && !health.data ? (
        <LoadingPanel message="Checking service health..." />
      ) : health.error && !health.data ? (
        <ErrorPanel message={health.error} onRetry={health.refresh} />
      ) : health.data ? (
        <>
          {/* Service Status Grid */}
          <div className="adm-card">
            <div className="adm-card-title">Service Dependencies</div>
            <div className="adm-service-grid">
              {Object.entries(health.data.services).map(([key, svc]) => (
                <div key={key} className="adm-service-card">
                  <div className="adm-service-header">
                    <span className="adm-service-name">{SERVICE_LABELS[key] ?? key}</span>
                    <StatusBadge status={svc.status as ServiceStatus} />
                  </div>
                  <div className="adm-service-metric">
                    <span className="adm-mono adm-metric-value">{svc.latency ?? '—'}</span>
                    <span className="adm-metric-unit">ms</span>
                  </div>
                  {svc.error && (
                    <div className="adm-service-error">{svc.error}</div>
                  )}
                </div>
              ))}
              {/* Graph API — derived from webhook sub status */}
              <div className="adm-service-card">
                <div className="adm-service-header">
                  <span className="adm-service-name">Microsoft Graph API</span>
                  <StatusBadge
                    status={
                      webhooks.data && webhooks.data.subscriptions.length > 0
                        ? 'healthy'
                        : webhooks.error
                          ? 'down'
                          : 'unknown'
                    }
                  />
                </div>
                <div className="adm-service-metric">
                  <span className="adm-mono adm-metric-value">
                    {webhooks.data?.subscriptions.length ?? 0}
                  </span>
                  <span className="adm-metric-unit">subscriptions</span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Overview */}
          <div className="adm-card">
            <div className="adm-card-title">Performance (7-day)</div>
            <div className="adm-perf-grid">
              <div className="adm-perf-item">
                <span className="adm-perf-label">Uptime</span>
                <span className="adm-mono adm-perf-value">
                  {health.data.uptime.percentage_7d.toFixed(2)}%
                </span>
              </div>
              <div className="adm-perf-item">
                <span className="adm-perf-label">Avg Response</span>
                <span className="adm-mono adm-perf-value">
                  {health.data.performance.avg_response_time.toFixed(0)}ms
                </span>
              </div>
              <div className="adm-perf-item">
                <span className="adm-perf-label">P95 Response</span>
                <span className="adm-mono adm-perf-value">
                  {health.data.performance.p95_response_time.toFixed(0)}ms
                </span>
              </div>
              <div className="adm-perf-item">
                <span className="adm-perf-label">Error Rate</span>
                <span className="adm-mono adm-perf-value">
                  {health.data.performance.error_rate.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Webhook Subscriptions */}
          {webhooks.data && (
            <WebhookPanel subscriptions={webhooks.data.subscriptions} />
          )}
          {webhooks.error && !webhooks.data && (
            <div className="adm-card">
              <div className="adm-card-title">Graph Webhook Subscriptions</div>
              <ErrorPanel message={webhooks.error} onRetry={webhooks.refresh} />
            </div>
          )}

          {/* Index Health */}
          <div className="adm-card">
            <div className="adm-card-title">Index Health</div>
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Index</th>
                  <th>Documents</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600 }}>devon-knowledge-index</td>
                  <td className="adm-mono">
                    {health.data.document_count?.toLocaleString() ?? '—'}
                  </td>
                  <td><StatusBadge status="healthy" /></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>devgpt-ephemeral-index</td>
                  <td className="adm-mono">—</td>
                  <td>
                    <span className="adm-badge-info">Provisioned / Inactive</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};
